import { useState } from 'react';
import { Alert, Button, ButtonGroup, Col, Form, Row } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import SearchableSelect from './SearchableSelect';
import PhoneNumberInput, { isValidPhoneNumber } from './PhoneNumberInput';
import { formatCustomerDisplayName } from '../utils/customerDisplayName';

const customerLabel = (c) => {
  const contact = c.phoneNumber || c.email;
  return `${formatCustomerDisplayName(c)}${contact ? ` — ${contact}` : ''}`;
};

/**
 * Customer choice for a Sale Bill, deliberately an either/or:
 *  - "Customer details": search existing customers by name/phone/email, or quick-create one
 *    from just a phone or an email (name optional).
 *  - "Walk-in": no buyer details are captured at all - the bill goes to the org's placeholder
 *    Walk-in Customer.
 * Mode and the walk-in placeholder are owned by the parent so it can save/restore them.
 */
function BillCustomerPicker({ mode, onModeChange, customers, value, onChange, onCreated, disabled }) {
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const options = customers.map((c) => ({
    value: c.id,
    label: customerLabel(c),
    keywords: `${c.phoneNumber || ''} ${c.email || ''} ${c.companyName || ''}`
  }));

  const resetNewForm = () => {
    setNewName('');
    setNewPhone('');
    setNewEmail('');
    setShowNewForm(false);
  };

  const handleCreate = async () => {
    const phone = (newPhone || '').trim();
    const email = newEmail.trim();
    if (!phone && !email) {
      toast.error('Enter a phone number or an email');
      return;
    }
    if (phone && !isValidPhoneNumber(phone)) {
      toast.error("That phone number doesn't look valid");
      return;
    }
    const existing = customers.find(
      (c) =>
        (phone && c.phoneNumber && c.phoneNumber.trim() === phone) ||
        (email && c.email && c.email.trim().toLowerCase() === email.toLowerCase())
    );
    if (existing) {
      toast.info(`${formatCustomerDisplayName(existing)} already exists - selected`);
      onChange(String(existing.id));
      resetNewForm();
      return;
    }
    setSaving(true);
    try {
      const payload = {
        firstName: newName.trim() || phone || email,
        customerType: 'INDIVIDUAL'
      };
      if (phone) payload.phoneNumber = phone;
      if (email) payload.email = email;
      const response = await axios.post('/api/customers', payload);
      toast.success('Customer created');
      onCreated(response.data);
      resetNewForm();
    } catch (error) {
      const msg = error.response?.data;
      toast.error(typeof msg === 'string' && msg ? msg : 'Error creating customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <ButtonGroup className="w-100 mb-2" aria-label="Customer choice">
        <Button
          type="button"
          size="sm"
          variant={mode === 'details' ? 'primary' : 'outline-secondary'}
          onClick={() => onModeChange('details')}
          disabled={disabled}
        >
          Customer details
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === 'walkin' ? 'warning' : 'outline-secondary'}
          onClick={() => onModeChange('walkin')}
          disabled={disabled}
        >
          Walk-in (no details)
        </Button>
      </ButtonGroup>

      {mode === 'walkin' ? (
        <Alert variant="warning" className="py-2 mb-0 small">
          <strong>Walk-in sale.</strong> No customer details are recorded on this bill - no name,
          phone or email, and it won&apos;t appear in any customer&apos;s history. To record who
          bought, switch to <em>Customer details</em>.
        </Alert>
      ) : (
        <>
          <SearchableSelect
            options={options}
            value={value}
            onChange={onChange}
            required
            disabled={disabled}
            placeholder="Search by name, phone or email"
            emptyLabel="No matching customer - add a new one below"
          />
          {!showNewForm ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="p-0 mt-1"
              onClick={() => setShowNewForm(true)}
              disabled={disabled}
            >
              + New customer (phone or email)
            </Button>
          ) : (
            <div className="border rounded p-2 mt-2">
              <Row className="g-2">
                <Col xs={12}>
                  <Form.Label className="small mb-1">Phone</Form.Label>
                  <PhoneNumberInput name="newPhone" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
                </Col>
                <Col xs={12}>
                  <Form.Label className="small mb-1">Email</Form.Label>
                  <Form.Control
                    type="email"
                    size="sm"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Label className="small mb-1">Name (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Defaults to the phone or email"
                  />
                </Col>
              </Row>
              <div className="d-flex gap-2 mt-2">
                <Button type="button" size="sm" onClick={handleCreate} disabled={saving}>
                  {saving ? 'Creating…' : 'Create & select'}
                </Button>
                <Button type="button" size="sm" variant="outline-secondary" onClick={resetNewForm} disabled={saving}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default BillCustomerPicker;
